import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import InAppBrowserBanner from "@/components/InAppBrowserBanner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import EmployeeDashboard from "@/pages/employee/EmployeeDashboard";
import TimeClock from "@/pages/employee/TimeClock";
import JobEntries from "@/pages/employee/JobEntries";
import FinalizeDay from "@/pages/employee/FinalizeDay";
import WeeklySummary from "@/pages/employee/WeeklySummary";
import EmployeeDayEdit from "@/pages/employee/EmployeeDayEdit";
import Paystubs from "@/pages/employee/Paystubs";
import Reimbursements from "@/pages/employee/Reimbursements";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import { AdminSubmissions, AdminSubmissionDetail } from "@/pages/admin/AdminSubmissions";
import AdminPayroll from "@/pages/admin/AdminPayroll";
import { AdminPaystubs, AdminPaystubDetail } from "@/pages/admin/AdminPaystubsPage";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTaxes from "@/pages/admin/AdminTaxes";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminReimbursements from "@/pages/admin/AdminReimbursements";
import AdminShopTime from "@/pages/admin/AdminShopTime";
import AdminDailyPunches from "@/pages/admin/AdminDailyPunches";
import SuperAdminAllUsers from "@/pages/admin/SuperAdminAllUsers";
import SuperAdminTenants from "@/pages/admin/SuperAdminTenants";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminHandbook from "@/pages/admin/AdminHandbook";
import AdminIndustry from "@/pages/admin/AdminIndustry";
const DemoPage = lazy(() => import("@/pages/public/DemoPage"));
const HvacLanding = lazy(() => import("@/pages/public/HvacLanding"));
const TermsPage = lazy(() => import("@/pages/public/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/public/PrivacyPage"));
import SignupWizard from "@/pages/public/SignupWizard";
import VerifyEmail from "@/pages/public/VerifyEmail";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";

// Growth Center — lazy-loaded; distinct sibling environment.
import GrowthLayout from "@/pages/growth/GrowthLayout";
import GrowthCommandCenter from "@/pages/growth/GrowthCommandCenter";
import GrowthOpportunities from "@/pages/growth/GrowthOpportunities";
import { GrowthLeads, GrowthPipeline } from "@/pages/growth/GrowthLeadsAndPipeline";
import GrowthOutreach from "@/pages/growth/GrowthOutreach";
import { GrowthActivation, GrowthAgentLog } from "@/pages/growth/GrowthActivationAndLog";
import GrowthSettings from "@/pages/growth/GrowthSettings";
import GrowthSocialPosts from "@/pages/growth/GrowthSocialPosts";
import {
  GrowthContent, GrowthPartnerships, GrowthCommunities,
  GrowthAffiliates, GrowthTestimonials, GrowthInsights, GrowthAnalytics,
} from "@/pages/growth/GrowthStubs";

function PublicHome() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>}>
      <DemoPage />
    </Suspense>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <PublicHome />;
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
}

// Meta Pixel PageView on every SPA route change. Guarded with
// `if (window.fbq)` so route changes are safe even when the pixel is
// blocked by an ad-blocker or hasn't loaded yet. Fires on every pathname
// change, including the initial render after mount — the <head> script
// already fires the first PageView, so the mount-time call here is a
// no-op duplicate that Meta dedupes.
function PixelPageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandingProvider>
          <BrowserRouter>
          <InAppBrowserBanner />
          <PixelPageViewTracker />
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/signup" element={<SignupWizard />} />
            <Route path="/terms" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="h-10 w-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" /></div>}>
                <TermsPage />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="h-10 w-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" /></div>}>
                <PrivacyPage />
              </Suspense>
            } />
            <Route path="/demo" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400">Loading demo…</div>}>
                <DemoPage />
              </Suspense>
            } />
            <Route path="/hvac" element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>}>
                <HvacLanding />
              </Suspense>
            } />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/login" element={<Login />} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<ProtectedRoute requiresSubscription><EmployeeDashboard /></ProtectedRoute>} />
              <Route path="/time" element={<ProtectedRoute requiresSubscription><TimeClock /></ProtectedRoute>} />
              <Route path="/jobs" element={<ProtectedRoute requiresSubscription><JobEntries /></ProtectedRoute>} />
              <Route path="/finalize" element={<ProtectedRoute requiresSubscription><FinalizeDay /></ProtectedRoute>} />
              <Route path="/week" element={<ProtectedRoute requiresSubscription><WeeklySummary /></ProtectedRoute>} />
              <Route path="/week/edit/:id" element={<ProtectedRoute requiresSubscription><EmployeeDayEdit /></ProtectedRoute>} />
              <Route path="/paystubs" element={<ProtectedRoute requiresSubscription><Paystubs /></ProtectedRoute>} />
              <Route path="/reimbursements" element={<ProtectedRoute requiresSubscription><Reimbursements /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute adminOnly requiresSubscription><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/employees" element={<ProtectedRoute adminOnly requiresSubscription><AdminEmployees /></ProtectedRoute>} />
              <Route path="/admin/submissions" element={<ProtectedRoute adminOnly requiresSubscription><AdminSubmissions /></ProtectedRoute>} />
              <Route path="/admin/submissions/day" element={<ProtectedRoute adminOnly requiresSubscription><AdminSubmissionDetail /></ProtectedRoute>} />
              <Route path="/admin/submissions/:id" element={<ProtectedRoute adminOnly requiresSubscription><AdminSubmissionDetail /></ProtectedRoute>} />
              <Route path="/admin/daily-punches" element={<ProtectedRoute adminOnly requiresSubscription><AdminDailyPunches /></ProtectedRoute>} />
              <Route path="/admin/shop-time" element={<ProtectedRoute adminOnly requiresSubscription><AdminShopTime /></ProtectedRoute>} />
              <Route path="/admin/payroll" element={<ProtectedRoute adminOnly requiresSubscription><AdminPayroll /></ProtectedRoute>} />
              <Route path="/admin/paystubs" element={<ProtectedRoute adminOnly requiresSubscription><AdminPaystubs /></ProtectedRoute>} />
              <Route path="/admin/paystubs/:id" element={<ProtectedRoute adminOnly requiresSubscription><AdminPaystubDetail /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute adminOnly requiresSubscription><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute adminOnly requiresSubscription><AdminSettings /></ProtectedRoute>} />
              {/* /admin/billing is the ONE admin route that stays open without a
                  subscription — this is where they subscribe. */}
              <Route path="/admin/billing" element={<ProtectedRoute adminOnly><AdminBilling /></ProtectedRoute>} />
              <Route path="/admin/taxes" element={<ProtectedRoute adminOnly requiresSubscription><AdminTaxes /></ProtectedRoute>} />
              <Route path="/admin/audit" element={<ProtectedRoute adminOnly requiresSubscription><AdminAuditLog /></ProtectedRoute>} />
              <Route path="/admin/handbook" element={<ProtectedRoute adminOnly requiresSubscription><AdminHandbook /></ProtectedRoute>} />
              <Route path="/admin/industry" element={<ProtectedRoute adminOnly requiresSubscription><AdminIndustry /></ProtectedRoute>} />
              <Route path="/admin/reimbursements" element={<ProtectedRoute adminOnly requiresSubscription><AdminReimbursements /></ProtectedRoute>} />
              <Route path="/admin/all-users" element={<ProtectedRoute superAdminOnly><SuperAdminAllUsers /></ProtectedRoute>} />
              <Route path="/admin/tenants" element={<ProtectedRoute superAdminOnly><SuperAdminTenants /></ProtectedRoute>} />
            </Route>

            {/* ───── Growth Center (distinct layout, super_admin + growth_manager) ───── */}
            <Route element={<ProtectedRoute growthOnly><GrowthLayout /></ProtectedRoute>}>
              <Route path="/growth"                element={<GrowthCommandCenter />} />
              <Route path="/growth/opportunities"  element={<GrowthOpportunities />} />
              <Route path="/growth/leads"          element={<GrowthLeads />} />
              <Route path="/growth/pipeline"       element={<GrowthPipeline />} />
              <Route path="/growth/outreach"       element={<GrowthOutreach />} />
              <Route path="/growth/activation"     element={<GrowthActivation />} />
              <Route path="/growth/social"         element={<GrowthSocialPosts />} />
              <Route path="/growth/agent-log"      element={<GrowthAgentLog />} />
              <Route path="/growth/settings"       element={<GrowthSettings />} />
              <Route path="/growth/content"        element={<GrowthContent />} />
              <Route path="/growth/partnerships"   element={<GrowthPartnerships />} />
              <Route path="/growth/communities"    element={<GrowthCommunities />} />
              <Route path="/growth/affiliates"     element={<GrowthAffiliates />} />
              <Route path="/growth/testimonials"   element={<GrowthTestimonials />} />
              <Route path="/growth/analytics"      element={<GrowthAnalytics />} />
              <Route path="/growth/insights"       element={<GrowthInsights />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
